/**
 * Bilinear upscaling on raw RGBA buffers.
 *
 * This used to be done with repeated `ctx.drawImage` calls on a canvas, which
 * meant it could only run wherever a canvas existed — i.e. on the main thread,
 * where a 4x upscale of a large photo locks up the page for seconds. Doing the
 * resample here instead makes it pure and therefore safe to run in a worker,
 * and directly unit-testable.
 *
 * Alpha is resampled alongside the colour channels rather than copied, so
 * already-transparent regions stay correctly transparent.
 */

export interface RgbaImage {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export function createRgbaImage(width: number, height: number): RgbaImage {
  return { data: new Uint8ClampedArray(width * height * 4), width, height };
}

/**
 * Resample to an exact destination size in a single bilinear step.
 *
 * Edges are clamped rather than wrapped, which matters here: a wrapped sample
 * would pull pixels in from the opposite edge and put a visible seam along the
 * border of an upscaled image.
 */
export function resampleBilinear(
  src: Uint8ClampedArray,
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(dstW * dstH * 4);
  if (srcW === dstW && srcH === dstH) {
    out.set(src);
    return out;
  }

  // Sample at pixel centres so the mapping is symmetric about the image.
  const scaleX = srcW / dstW;
  const scaleY = srcH / dstH;

  for (let y = 0; y < dstH; y++) {
    const sy = (y + 0.5) * scaleY - 0.5;
    const y0 = Math.floor(sy);
    const fy = sy - y0;
    const y0c = clampIndex(y0, srcH);
    const y1c = clampIndex(y0 + 1, srcH);
    const row0 = y0c * srcW;
    const row1 = y1c * srcW;
    const dstRow = y * dstW;

    for (let x = 0; x < dstW; x++) {
      const sx = (x + 0.5) * scaleX - 0.5;
      const x0 = Math.floor(sx);
      const fx = sx - x0;
      const x0c = clampIndex(x0, srcW);
      const x1c = clampIndex(x0 + 1, srcW);

      const i00 = (row0 + x0c) << 2;
      const i01 = (row0 + x1c) << 2;
      const i10 = (row1 + x0c) << 2;
      const i11 = (row1 + x1c) << 2;
      const o = (dstRow + x) << 2;

      const w00 = (1 - fx) * (1 - fy);
      const w01 = fx * (1 - fy);
      const w10 = (1 - fx) * fy;
      const w11 = fx * fy;

      out[o] = src[i00] * w00 + src[i01] * w01 + src[i10] * w10 + src[i11] * w11;
      out[o + 1] = src[i00 + 1] * w00 + src[i01 + 1] * w01 + src[i10 + 1] * w10 + src[i11 + 1] * w11;
      out[o + 2] = src[i00 + 2] * w00 + src[i01 + 2] * w01 + src[i10 + 2] * w10 + src[i11 + 2] * w11;
      out[o + 3] = src[i00 + 3] * w00 + src[i01 + 3] * w01 + src[i10 + 3] * w10 + src[i11 + 3] * w11;
    }
  }

  return out;
}

function clampIndex(value: number, limit: number): number {
  if (value < 0) return 0;
  if (value >= limit) return limit - 1;
  return value;
}

/**
 * Upscale in increments of at most 2x until the target size is reached.
 *
 * Stepping is what makes the result look good: each bilinear step is high
 * quality, and compounding several is noticeably sharper than one large jump.
 */
export function stepUpscale(
  src: Uint8ClampedArray,
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number,
  onProgress?: (step: number, of: number) => void
): Uint8ClampedArray {
  let current = src;
  let curW = srcW;
  let curH = srcH;

  const totalSteps = Math.max(
    Math.ceil(Math.log2(Math.max(1, dstW / srcW))),
    Math.ceil(Math.log2(Math.max(1, dstH / srcH)))
  );
  let step = 0;

  while (curW < dstW || curH < dstH) {
    const nextW = Math.min(dstW, curW * 2);
    const nextH = Math.min(dstH, curH * 2);
    current = resampleBilinear(current, curW, curH, nextW, nextH);
    curW = nextW;
    curH = nextH;
    step++;
    onProgress?.(step, totalSteps);
  }

  return current;
}